import React, { useMemo } from 'react';
import { Helmet } from 'react-helmet';
import { useStaticQuery, graphql } from 'gatsby';
import { decodeEntities } from '../utils';
import sgTwilioLockupSocialPng from '../assets/images/SG_Twilio_Lockup_Social.png';

const SEO = ({ page, path, children, ...props }) => {
  const { seo, date, modified } = page;
  const {
    site: { siteMetadata },
  } = useStaticQuery(graphql`
    query {
      site {
        siteMetadata {
          title
          titleTemplate
          description
          siteUrl
        }
      }
    }
  `);

  const optimizePathsProd = [
    '/solutions/email-marketing/',
    '/solutions/email-api/smtp-service/',
    '/solutions/email-marketing/automation/',
  ];

  const noIndexPaths = [
    '/solutions/homepage-v2/',
    '/solutions/homepage-v3/',
  ];

  const title = useMemo(() => {
    if (seo.title)
      return decodeEntities(seo.title).replace(' | SendGrid', '').trim();
    if (props.title) return props.title;
    return siteMetadata.title;
  }, [props.title]);

  const description =
    seo.metaDesc || props.description || siteMetadata.description;

  const canonical = path ? siteMetadata.siteUrl + path : null;

  const image = page.featuredImage
    ? page.featuredImage.imageFile.publicURL
    : sgTwilioLockupSocialPng;

  const ogImage = seo.opengraphImage
    ? seo.opengraphImage.imageFile.publicURL
    : image;

  const robots = [];
  if (seo.metaRobotsNoindex === '1') robots.push('noindex');
  if (seo.metaRobotsNofollow === '1') robots.push('nofollow');

  const jsonLinkedData = useMemo(() => {
    if (!canonical) return null;
    const jsonData = {
      '@context': 'https://schema.org',
      '@graph': [
        {
          '@type': 'Organization',
          '@id': 'https://sendgrid.com/#organization',
          name: 'Twilio Sendgrid',
          url: 'https://sendgrid.com/',
          sameAs: [
            'https://twitter.com/sendgrid',
            'https://www.linkedin.com/company/sendgrid/',
            'https://en.wikipedia.org/wiki/SendGrid',
          ],
          logo: {
            '@type': 'ImageObject',
            url: image,
          },
          parentOrganization: {
            '@type': 'Organization',
            name: 'Twilio',
            url: 'https://www.twilio.com',
          },
          founder: [
            { '@type': 'Person', name: 'Isaac Saldana' },
            { '@type': 'Person', name: 'Jose Lopez' },
            { '@type': 'Person', name: 'Tim Jenkins' },
          ],
          contactPoint: {
            '@type': 'ContactPoint',
            telephone: '+1-888-985-7363',
            url: 'https://sendgrid.com/contact-us-form/',
            contactType: 'Customer service',
          },
        },
        {
          '@type': 'WebSite',
          '@id': 'https://sendgrid.com/#website',
          url: 'https://sendgrid.com/',
          name: 'SendGrid',
          inLanguage: 'en-US',
          description: 'We Make Email Delivery Easy',
          publisher: { '@id': 'https://sendgrid.com/#organization' },
          potentialAction: {
            '@type': 'SearchAction',
            target: 'https://sendgrid.com/?s={search_term_string}',
            'query-input': 'required name=search_term_string',
          },
        },
      ],
    };
    if (date && modified) {
      jsonData['@graph'].push({
        '@type': 'WebPage',
        '@id': `${canonical}#webpage`,
        url: canonical,
        name: title,
        isPartOf: { '@id': 'https://sendgrid.com/#website' },
        inLanguage: 'en-US',
        datePublished: date,
        dateModified: modified,
        description,
      });
    }
    return JSON.stringify(jsonData);
  }, [canonical]);

  return (
    <Helmet title={title} titleTemplate={siteMetadata.titleTemplate}>
      <script async="async" src="//consent.trustarc.com/notice?domain=sendgrid.com&c=teconsent&js=nj&noticeType=bb&gtm=1" crossOrigin />
      <link rel="stylesheet" type="text/css" href="https://cloud.typography.com/6230892/752864/css/fonts.css" />

      {/* General Tags */}
      {canonical && <link rel="canonical" href={canonical} />}
      <meta name="description" content={description} />
      <meta name="image" content={image} />
      <meta
        name="robots"
        content={
          robots.length > 0
            ? robots.join(',')
            : 'max-snippet:-1, max-image-preview:large, max-video-preview:-1'
        }
      />
      <meta name="google-site-verification" content="1Djcw0U3W2F8Elwhcnr322sqcjvmCmiqPv0N0xCn0To" />

      {/* Google optimize */}
      {optimizePathsProd.includes(path) && (
        <script src="https://www.googleoptimize.com/optimize.js?id=OPT-PJ48CL4" />
      )}

      {/* No Index Tag */}
      {noIndexPaths.includes(path) && (
        <meta name="robots" content="noindex" />
      )}

      {/* OpenGraph Tags */}
      {canonical && <meta property="og:url" content={canonical} />}
      <meta property="og:locale" content="en_US" />
      <meta property="og:type" content="article" />
      <meta property="og:site_name" content="SendGrid" />
      <meta property="og:image" content={ogImage} />
      <meta property="og:image:secure_url" content={ogImage} />
      <meta property="og:title" content={seo.opengraphTitle || title} />
      <meta
        property="og:description"
        content={seo.opengraphDescription || description}
      />

      <meta name="instapage-verification" content="1598286911558-IC46" />

      {/* Schema.org tags */}
      {jsonLinkedData && (
        <script type="application/ld+json">{jsonLinkedData}</script>
      )}

      {children}
    </Helmet>
  );
};

SEO.defaultProps = {
  page: { seo: {} },
};

export default SEO;

export const query = graphql`
  fragment SEOFragment on WPGraphQL_SEO {
    title
    metaDesc
    metaRobotsNoindex
    metaRobotsNofollow
    opengraphTitle
    opengraphDescription
    opengraphImage {
      sourceUrl
    }
  }
  fragment SEOPageFragment on WPGraphQL_Page {
    date
    modified
    featuredImage {
      node {
        sourceUrl
        mediaItemUrl
      }
    }
    seo {
      title
      metaDesc
      metaRobotsNoindex
      metaRobotsNofollow
      opengraphTitle
      opengraphDescription
      opengraphImage {
        sourceUrl
      }
    }
  }
  fragment UseCaseSEOPageFragment on WPGraphQL_UseCase {
    date
    modified
    featuredImage {
      node {
        sourceUrl
      }
    }
    seo {
      title
      metaDesc
      metaRobotsNoindex
      metaRobotsNofollow
      opengraphTitle
      opengraphDescription
      opengraphImage {
        sourceUrl
      }
    }
  }
  fragment SEOVideoFragment on WPGraphQL_Video {
    date
    modified
    seo {
      title
      metaDesc
      metaRobotsNoindex
      metaRobotsNofollow
      opengraphTitle
      opengraphDescription
      opengraphImage {
        sourceUrl
      }
    }
  }
`;